import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, Youtube, Facebook, Calendar, Clock } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MINISTRY_BANNER = 'https://static.prod-images.emergentagent.com/jobs/9bd4de2a-403c-4211-ba6a-7e7952c06d35/images/0f840a9da0fc7fdcf5f799cedc92d5d0049c007d763102e1d4304916e6d82131.png';

export default function LivestreamPage() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    is_live: false,
    youtube_url: '',
    facebook_url: '',
    title: 'Live Worship Service',
    scheduled_time: 'Sunday at 10:00 AM'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/livestream`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching livestream settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|live\/)|youtu\.be\/)([^&\s]+)/);
    if (match) {
      return `https://www.youtube.com/embed/${match[1]}?autoplay=1`;
    }
    return url;
  };

  return (
    <div className="min-h-screen bg-charcoal pt-20" data-testid="livestream-page">
      {/* Ministry Banner */}
      <div className="w-full">
        <img 
          src={MINISTRY_BANNER}
          alt="Living Fruit Ministry"
          className="w-full h-auto object-cover"
          style={{ maxHeight: '300px', objectPosition: 'center' }}
        />
      </div>

      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">{t('livestream.subtitle')}</span>
            <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">{t('livestream.title')}</h1>
          </motion.div>

          {/* Video Player */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            {settings.is_live && settings.youtube_url ? (
              <div className="relative">
                {/* Live Badge */}
                <div className="absolute top-4 left-4 z-10 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-2">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  {t('livestream.live')}
                </div>
                
                <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                  <iframe
                    src={getYouTubeEmbedUrl(settings.youtube_url)}
                    width="100%"
                    height="100%"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    title="Live Stream"
                    className="w-full h-full"
                  />
                </div>
              </div>
            ) : (
              <div className="aspect-video bg-charcoal/50 rounded-2xl border border-gold/20 flex flex-col items-center justify-center">
                <div className="w-20 h-20 bg-burgundy/20 rounded-full flex items-center justify-center mb-6">
                  <Play size={40} className="text-burgundy ml-2" />
                </div>
                <h2 className="font-serif text-2xl text-white mb-2">{t('livestream.notLive')}</h2>
                <p className="text-white/60 mb-6">
                  {t('livestream.nextService')} <span className="text-gold">{settings.scheduled_time || 'Sunday at 10:00 AM'}</span>
                </p>
                
                <div className="flex gap-4">
                  {settings.youtube_url && (
                    <a
                      href={settings.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full transition-colors"
                    >
                      <Youtube size={20} />
                      {t('livestream.watchOnYouTube')}
                    </a>
                  )}
                  {settings.facebook_url && (
                    <a
                      href={settings.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full transition-colors"
                    >
                      <Facebook size={20} />
                      {t('livestream.watchOnFacebook')}
                    </a>
                  )}
                </div>
              </div>
            )}
          </motion.div>

          {/* Service Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 text-center"
          >
            <div className="inline-flex items-center gap-6 bg-white/5 backdrop-blur-sm rounded-full px-8 py-4">
              <div className="flex items-center gap-2 text-white/70">
                <Calendar size={18} className="text-gold" />
                <span>Sundays</span>
              </div>
              <div className="w-px h-6 bg-white/20" />
              <div className="flex items-center gap-2 text-white/70">
                <Clock size={18} className="text-gold" />
                <span>10:00 AM EST</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Past Services */}
      <section className="py-16 bg-cream">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex justify-between items-center mb-8">
            <h2 className="font-serif text-2xl text-charcoal">{t('livestream.pastServices')}</h2>
            <Link to="/sermons" className="text-burgundy hover:text-purple transition-colors">
              {t('livestream.viewAll')} →
            </Link>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'The Fruit of the Spirit', date: 'Dec 1, 2024' },
              { title: 'Living as Salt and Light', date: 'Nov 24, 2024' },
              { title: 'Faith in Action', date: 'Nov 17, 2024' },
            ].map((sermon, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl overflow-hidden border border-gold/10 hover:border-gold/30 transition-all duration-300"
              >
                <div className="aspect-video bg-charcoal/5 flex items-center justify-center">
                  <div className="w-12 h-12 bg-burgundy/10 rounded-full flex items-center justify-center">
                    <Play size={20} className="text-burgundy ml-1" />
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-serif text-lg text-charcoal mb-1">{sermon.title}</h3>
                  <p className="text-sm text-charcoal/50">{sermon.date}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
